import {dia} from '@joint/core';

/**
 * Exports of the apps groups view: an SVG picture, a PNG picture, and a draw.io diagram.
 *
 * The three of them are built from the JointJS model rather than from what the paper drew. The nodes of the view
 * are Angular components laid in 'foreignObject' elements, which only browsers know how to draw: an SVG holding
 * them shows empty boxes in any other tool, and loses whatever the stylesheets of the application said about
 * them. The model holds every position, size and nesting, and the paper the route every link ended up with,
 * which is all an export needs to draw the graph on its own.
 *
 * The exports are always light: they are meant for documents and diagrams, whatever the theme of the view.
 */

export type AppsGroupsExportFormat = "svg" | "png" | "drawio";

type ExportedKind = "group" | "included" | "member";

class ExportedElement {
  id: string;
  kind: ExportedKind;
  label: string;
  // Second line of a closed box of an included apps group ('3 members')
  detail?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  parentId?: string;
}

class ExportedLink {
  id: string;
  sourceId: string;
  targetId: string;
  // Path of the link as drawn in the view, in the coordinates of the graph
  path: string;
  // Points the link goes through, its two ends included
  points: {x: number, y: number}[];
  sourceArrow: boolean;
  targetArrow: boolean;
  // As drawn in the view, the flows descriptions colouring the links and naming them
  color: string;
  dashed: boolean;
  label?: string;
}

const STYLES = {
  group: {fill: "#f2f7fb", header: "#e8f0fa", stroke: "#5b6dae", dashed: false},
  included: {fill: "#faf6ec", header: "#f5ecd6", stroke: "#5b6dae", dashed: true},
  member: {fill: "#ffffff", header: "#ffffff", stroke: "#5b6dae", dashed: false},
  link: "#525e5e",
  text: "#333333",
  detail: "#666666",
  background: "#ffffff",
  font: "Arial, Helvetica, sans-serif",
  fontSize: 12,
  // Height of the band holding the name of a group or of a box, children being laid below it
  headerHeight: 24,
};

// Room left around the graph, for the strokes and the arrows of the elements sitting on its edges
const PADDING = 20;

// Browsers refuse canvases past these sizes, and a larger PNG is rather a job for the SVG anyway
const MAX_CANVAS_SIDE = 16000;
const MAX_CANVAS_AREA = 16000 * 16000 / 2;

export class AppsGroupsGraphExport {
  private elements: ExportedElement[] = [];
  private links: ExportedLink[] = [];

  constructor(private graph: dia.Graph, private paper: dia.Paper) {
    this.readGraph();
  }

  export = async (format: AppsGroupsExportFormat) => {
    let fileNames: Record<AppsGroupsExportFormat, string> = {svg: "graph.svg", png: "graph.png", drawio: "graph.drawio"};
    this.download(await this.toBlob(format), fileNames[format]);
  }

  /**
   * The file of the given format, nothing when the graph holds nothing: what 'export' downloads, and what the
   * reports read through the page (see AppsGroupsMapComponent.registerReportsHooks).
   */
  toBlob = async (format: AppsGroupsExportFormat): Promise<Blob> => {
    if (this.elements.length == 0) {
      console.warn("[apps-groups] the graph holds nothing to export");
      return undefined;
    }

    if (format == "svg") {
      return new Blob([this.toSvg()], {type: "image/svg+xml"});
    } else if (format == "png") {
      return await this.toPng(2);
    } else {
      return new Blob([this.toDrawio()], {type: "application/xml"});
    }
  }

  //////////////////////////////
  // Reading the graph
  //////////////////////////////

  private readGraph() {
    for (let element of this.graph.getElements()) {
      // @ts-ignore
      let data = element.attributes.data ?? {};
      let {x, y} = element.position();
      let {width, height} = element.size();

      let exported = new ExportedElement();
      exported.id = String(element.id);
      exported.kind = data.type == "member" ? "member" : data.type == "included" ? "included" : "group";
      exported.label = data.appGroup?.name ?? String(element.id);
      exported.x = x;
      exported.y = y;
      exported.width = width;
      exported.height = height;
      exported.depth = element.getAncestors().length;
      exported.parentId = element.getParentCell()?.id?.toString();

      let inclusion = data.appGroup?.inclusion;
      if (exported.kind == "included" && inclusion != undefined && !inclusion.expanded) {
        exported.detail = `${inclusion.memberUuids.length} members`;
      }

      this.elements.push(exported);
    }

    // Containers first, so that their children are drawn over them
    this.elements.sort((a, b) => a.depth - b.depth);

    for (let link of this.graph.getLinks()) {
      let linkView = this.paper.findViewByModel(link) as dia.LinkView;
      let source = link.getSourceElement();
      let target = link.getTargetElement();

      // A link the paper did not draw has no route to follow
      if (linkView == undefined || source == undefined || target == undefined) {
        continue;
      }

      let path = linkView.getSerializedConnection();
      if (path == undefined || path == "") {
        continue;
      }

      // @ts-ignore: the points the router and the anchors ended up with
      let {sourcePoint, targetPoint, route} = linkView;

      let exported = new ExportedLink();
      exported.id = String(link.id);
      exported.sourceId = String(source.id);
      exported.targetId = String(target.id);
      exported.path = path;
      exported.points = [sourcePoint, ...(route ?? []), targetPoint]
        .filter((point) => point != undefined)
        .map((point) => ({x: point.x, y: point.y}));
      exported.sourceArrow = !!link.attr("line/sourceMarker");
      exported.targetArrow = !!link.attr("line/targetMarker");
      exported.color = link.get("category")?.color ?? STYLES.link;
      exported.dashed = link.get("category")?.dashed ?? false;
      exported.label = link.get("category")?.label;

      this.links.push(exported);
    }
  }

  // Area the graph occupies, the routes of its links included
  private contentArea() {
    let xs: number[] = [];
    let ys: number[] = [];

    for (let element of this.elements) {
      xs.push(element.x, element.x + element.width);
      ys.push(element.y, element.y + element.height);
    }
    for (let link of this.links) {
      for (let point of link.points) {
        xs.push(point.x);
        ys.push(point.y);
      }
    }

    let x = Math.min(...xs) - PADDING;
    let y = Math.min(...ys) - PADDING;
    return {x, y, width: Math.max(...xs) + PADDING - x, height: Math.max(...ys) + PADDING - y};
  }

  //////////////////////////////
  // SVG
  //////////////////////////////

  private toSvg(): string {
    let area = this.contentArea();
    let parts: string[] = [];

    parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${area.width}" height="${area.height}" `
      + `viewBox="${area.x} ${area.y} ${area.width} ${area.height}" `
      + `font-family="${STYLES.font}" font-size="${STYLES.fontSize}">`);
    // One arrow per colour of link, a marker taking its colour from its definition
    let colors = Array.from(new Set(this.links.map((link) => link.color)));
    let markerIds = new Map(colors.map((color, index) => [color, `arrow${index}`]));
    parts.push(`<defs>` + colors.map((color) =>
      `<marker id="${markerIds.get(color)}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" `
      + `orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="${color}"/></marker>`).join("") + `</defs>`);
    parts.push(`<rect x="${area.x}" y="${area.y}" width="${area.width}" height="${area.height}" fill="${STYLES.background}"/>`);

    // The containers under the links, the members over them, as the view shows them
    let containers = this.elements.filter((element) => element.kind != "member");
    let members = this.elements.filter((element) => element.kind == "member");

    containers.forEach((element) => parts.push(this.svgElement(element)));

    for (let link of this.links) {
      let marker = markerIds.get(link.color);
      parts.push(`<path d="${escapeXml(link.path)}" fill="none" stroke="${link.color}" stroke-width="2"`
        + (link.dashed ? ` stroke-dasharray="8 5"` : "")
        + (link.sourceArrow ? ` marker-start="url(#${marker})"` : "")
        + (link.targetArrow ? ` marker-end="url(#${marker})"` : "")
        + `/>`);
    }

    // The names of the flows descriptions over the links, as the view writes them
    for (let link of this.links.filter((link) => link.label != undefined)) {
      let {x, y} = this.labelPosition(link);
      let width = Math.min(link.label.length, 30) * STYLES.fontSize * 0.6 + 8;
      parts.push(`<g><rect x="${x - width / 2}" y="${y - 8}" width="${width}" height="16" rx="3" fill="#ffffff" `
        + `stroke="${link.color}"/>`
        + this.svgText(link.label, x, y, width - 8, "middle", true, link.color) + `</g>`);
    }

    members.forEach((element) => parts.push(this.svgElement(element)));

    parts.push(`</svg>`);
    return parts.join("\n");
  }

  private svgElement(element: ExportedElement): string {
    let style = STYLES[element.kind];
    let {x, y, width, height} = element;
    let dash = style.dashed ? ` stroke-dasharray="5 3"` : "";
    let parts: string[] = [];

    parts.push(`<g>`);
    parts.push(`<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="4" fill="${style.fill}" `
      + `stroke="${style.stroke}"${dash}/>`);

    if (element.kind == "member") {
      // A member is a single name, centered in its node, on several lines when it is long as in the view
      let lines = wrapText(element.label, width - 10, STYLES.fontSize, 3);
      let lineHeight = STYLES.fontSize * 1.25;
      lines.forEach((line, index) => {
        let lineY = y + height / 2 + (index - (lines.length - 1) / 2) * lineHeight;
        parts.push(this.svgText(line, x + width / 2, lineY, width - 10, "middle"));
      });
    } else {
      let headerHeight = Math.min(STYLES.headerHeight, height);
      parts.push(`<path d="M ${x} ${y + headerHeight} H ${x + width}" stroke="${style.stroke}"${dash}/>`);
      parts.push(`<rect x="${x + 0.5}" y="${y + 0.5}" width="${Math.max(width - 1, 0)}" height="${headerHeight - 1}" `
        + `rx="4" fill="${style.header}"/>`);
      parts.push(this.svgText(element.label, x + 6, y + headerHeight / 2, width - 12, "start", true));

      if (element.detail != undefined) {
        parts.push(this.svgText(element.detail, x + 6, y + headerHeight + (height - headerHeight) / 2,
                                width - 12, "start", false, STYLES.detail));
      }
    }

    parts.push(`</g>`);
    return parts.join("");
  }

  // A line of text, cut to the width it is given as SVG text does not wrap
  private svgText(text: string, x: number, y: number, maxWidth: number, anchor: "start" | "middle",
                  bold = false, color = STYLES.text): string {
    return `<text x="${x}" y="${y}" dominant-baseline="central" text-anchor="${anchor}" fill="${color}"`
      + (bold ? ` font-weight="bold"` : "")
      + `>${escapeXml(fitText(text, maxWidth, STYLES.fontSize))}</text>`;
  }

  // Where the label of a link sits: at the place the view wrote it along the link, or halfway
  private labelPosition(link: ExportedLink): {x: number, y: number} {
    let jointLink = this.graph.getCell(link.id) as dia.Link;
    let linkView = jointLink != undefined ? this.paper.findViewByModel(jointLink) as dia.LinkView : undefined;
    let distance = jointLink?.label(0)?.position?.["distance"] ?? 0.5;

    let point = linkView?.getPointAtRatio(distance);
    if (point != undefined) {
      return {x: point.x, y: point.y};
    }

    let middle = link.points[Math.floor(link.points.length / 2)];
    return {x: middle.x, y: middle.y};
  }

  //////////////////////////////
  // PNG
  //////////////////////////////

  /**
   * Draws the SVG on a canvas. The SVG holds nothing but SVG, so the canvas stays readable in every browser,
   * which is not the case of a picture holding HTML ('foreignObject'). The scale is lowered on large graphs, as
   * browsers refuse canvases past a given size.
   */
  private async toPng(wantedScale: number): Promise<Blob> {
    let area = this.contentArea();
    let scale = Math.min(
      wantedScale,
      MAX_CANVAS_SIDE / area.width,
      MAX_CANVAS_SIDE / area.height,
      Math.sqrt(MAX_CANVAS_AREA / (area.width * area.height)),
    );

    let svgUrl = URL.createObjectURL(new Blob([this.toSvg()], {type: "image/svg+xml"}));

    try {
      let image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = svgUrl;
      });

      let canvas = document.createElement("canvas");
      canvas.width = Math.ceil(area.width * scale);
      canvas.height = Math.ceil(area.height * scale);

      let context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      return await new Promise<Blob>((resolve) => canvas.toBlob(resolve, "image/png"));
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  }

  //////////////////////////////
  // draw.io
  //////////////////////////////

  /**
   * Writes a draw.io diagram: the apps groups and the boxes of the apps groups they include are containers,
   * holding their members, and the links are connectors glued to the nodes they join. Moving a node in draw.io
   * therefore brings its links and its children along.
   *
   * draw.io places a child relative to its container, while a connector lying on the root layer takes absolute
   * points: the links are kept on the root layer, so that the route they had in the view can be given as is.
   */
  private toDrawio(): string {
    let area = this.contentArea();
    let elementsById = new Map(this.elements.map((element) => [element.id, element]));

    // draw.io ids are free text, but short ones keep the file readable
    let drawioIds = new Map<string, string>();
    this.elements.forEach((element, index) => drawioIds.set(element.id, `n${index}`));

    let cells: string[] = [];
    cells.push(`<mxCell id="0"/>`);
    cells.push(`<mxCell id="1" parent="0"/>`);

    for (let element of this.elements) {
      let parent = element.parentId != undefined ? elementsById.get(element.parentId) : undefined;
      let x = element.x - (parent?.x ?? area.x);
      let y = element.y - (parent?.y ?? area.y);

      cells.push(`<mxCell id="${drawioIds.get(element.id)}" value="${escapeXml(this.drawioLabel(element))}" `
        + `style="${this.drawioStyle(element)}" vertex="1" parent="${parent != undefined ? drawioIds.get(parent.id) : "1"}">`
        + `<mxGeometry x="${round(x)}" y="${round(y)}" width="${round(element.width)}" height="${round(element.height)}" as="geometry"/>`
        + `</mxCell>`);
    }

    this.links.forEach((link, index) => {
      let source = drawioIds.get(link.sourceId);
      let target = drawioIds.get(link.targetId);
      if (source == undefined || target == undefined) {
        return;
      }

      // The two ends are left to draw.io, which glues them to the nodes: only the bends are given
      let bends = link.points.slice(1, -1)
        .map((point) => `<mxPoint x="${round(point.x - area.x)}" y="${round(point.y - area.y)}"/>`)
        .join("");

      let style = [
        "edgeStyle=none", "rounded=1", "html=1", `strokeColor=${link.color}`, "strokeWidth=2",
        ...(link.dashed ? ["dashed=1"] : []),
        ...(link.label != undefined ? [`fontColor=${link.color}`, "fontStyle=1", "labelBackgroundColor=#ffffff", `fontSize=${STYLES.fontSize - 1}`] : []),
        `startArrow=${link.sourceArrow ? "block" : "none"}`, "startFill=1",
        `endArrow=${link.targetArrow ? "block" : "none"}`, "endFill=1",
      ].join(";") + ";";

      cells.push(`<mxCell id="e${index}" value="${escapeXml(link.label ?? "")}" style="${style}" edge="1" parent="1" source="${source}" target="${target}">`
        + `<mxGeometry relative="1" as="geometry">`
        + (bends != "" ? `<Array as="points">${bends}</Array>` : "")
        + `</mxGeometry></mxCell>`);
    });

    return `<?xml version="1.0" encoding="UTF-8"?>\n`
      + `<mxfile host="DC Netscope" type="device">`
      + `<diagram id="apps-groups" name="Apps groups">`
      + `<mxGraphModel dx="${round(area.width)}" dy="${round(area.height)}" grid="1" gridSize="10" guides="1" `
      + `tooltips="1" connect="1" arrows="1" fold="1" page="0" pageScale="1" math="0" shadow="0">`
      + `<root>${cells.join("")}</root>`
      + `</mxGraphModel></diagram></mxfile>\n`;
  }

  private drawioLabel(element: ExportedElement): string {
    return element.detail != undefined ? `${element.label}\n${element.detail}` : element.label;
  }

  private drawioStyle(element: ExportedElement): string {
    let style = STYLES[element.kind];
    let common = [
      "rounded=1", "arcSize=4", "absoluteArcSize=1", "whiteSpace=wrap", "html=0",
      `fillColor=${style.fill}`, `strokeColor=${style.stroke}`, `fontColor=${STYLES.text}`,
      `fontSize=${STYLES.fontSize}`,
    ];

    if (style.dashed) {
      common.push("dashed=1");
    }

    // A member, or a closed box standing for the members of its apps group: a single shape holding its name
    if (element.kind == "member" || element.detail != undefined) {
      return common.join(";") + ";";
    }

    // Groups and boxes are containers, their name in a header band as in the view
    return common.concat([
      "swimlane", "container=1", "collapsible=1", "fontStyle=1", "align=left", "spacingLeft=6",
      `startSize=${STYLES.headerHeight}`, `swimlaneFillColor=${style.fill}`, `fillColor=${style.header}`,
    ]).join(";") + ";";
  }

  //////////////////////////////
  // Download
  //////////////////////////////

  private download(blob: Blob, fileName: string) {
    if (blob == undefined) {
      console.warn(`[apps-groups] the export to ${fileName} produced nothing`);
      return;
    }

    let url = URL.createObjectURL(blob);
    let link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    .replace(/\n/g, "&#10;");
}

// Cuts a text to the width it is given, the width of a character being guessed from the size of the font
function fitText(text: string, maxWidth: number, fontSize: number): string {
  let maxCharacters = Math.floor(maxWidth / (fontSize * 0.6));
  if (text.length <= maxCharacters) {
    return text;
  }
  return maxCharacters <= 1 ? "…" : text.slice(0, maxCharacters - 1) + "…";
}

/**
 * Splits a text into the lines it takes in a given width, a line breaking after the separators of a name ('_', '-',
 * '.', ':') as the view lets it, and anywhere in a part longer than a line. The last line is cut when the text
 * needs more lines than given.
 */
function wrapText(text: string, maxWidth: number, fontSize: number, maxLines: number): string[] {
  let maxCharacters = Math.max(1, Math.floor(maxWidth / (fontSize * 0.6)));
  let lines: string[] = [""];

  for (let part of text.split(/(?<=[_\-.: ])/)) {
    // A part longer than a line is cut into pieces of a line each
    let pieces = part.match(new RegExp(`.{1,${maxCharacters}}`, "g")) ?? [part];
    for (let piece of pieces) {
      if ((lines[lines.length - 1] + piece).length > maxCharacters && lines[lines.length - 1] != "") {
        lines.push("");
      }
      lines[lines.length - 1] += piece;
    }
  }

  if (lines.length > maxLines) {
    lines = lines.slice(0, maxLines);
    lines[maxLines - 1] = fitText(lines[maxLines - 1] + "…", maxWidth, fontSize);
  }

  return lines.map((line) => line.trim());
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
